import { INTENT_LIMITS, isLinkableLocator, newContextReference, type ContextReference } from '@blueprint/domain'
import { Button, Field, IconButton, TextArea, TextInput } from '../../../components/controls.js'
import { useUiStore } from '../../../state/ui-store.js'
import styles from './intent.module.css'

interface Props {
  references: readonly ContextReference[]
  onChange: (next: ContextReference[]) => void
}

/**
 * Manually supplied card context. Nothing here is fetched, read or synchronised: a URL
 * or path is a pointer, and only a pasted excerpt is actually carried into a prompt.
 * This is not connected Teamcentric Context content.
 */
export function ReferencesEditor({ references, onChange }: Props) {
  const requestConfirm = useUiStore((state) => state.requestConfirm)

  const update = (id: string, patch: Partial<ContextReference>) =>
    onChange(references.map((reference) => (reference.id === id ? { ...reference, ...patch } : reference)))

  const remove = (reference: ContextReference) => {
    const drop = () => onChange(references.filter((item) => item.id !== reference.id))
    const hasContent = [reference.label, reference.locator, reference.relevance, reference.excerpt].some(
      (value) => value.trim().length > 0,
    )
    if (!hasContent) {
      drop()
      return
    }
    requestConfirm({
      title: 'Remove reference',
      message: `"${reference.label.trim() || 'This reference'}" and its excerpt will be removed from this card.`,
      consequence: 'To keep it but leave it out of a prompt, clear "Include in prompt" instead.',
      confirmLabel: 'Remove',
      undoable: true,
      onConfirm: drop,
    })
  }

  const atLimit = references.length >= INTENT_LIMITS.referenceCount

  return (
    <div className={styles.stack}>
      <p className={styles.guidance}>
        Links and paths are recorded as pointers. Blueprint does not open them, and an AI tool receiving the prompt
        will not have their contents unless you paste the relevant passage as an excerpt.
      </p>

      {references.map((reference, index) => (
        <section key={reference.id} className={styles.entry}>
          <header className={styles.entryHead}>
            <span className={styles.entryGroup}>Reference {index + 1}</span>
            <TextInput
              value={reference.label}
              aria-label={`Label for reference ${index + 1}`}
              placeholder="e.g. Order API conventions"
              maxLength={INTENT_LIMITS.referenceLabel}
              onChange={(event) => update(reference.id, { label: event.target.value })}
            />
            <label className={styles.include}>
              <input
                type="checkbox"
                checked={reference.include}
                onChange={(event) => update(reference.id, { include: event.target.checked })}
              />
              Include in prompt
            </label>
            <IconButton danger aria-label={`Remove reference ${index + 1}`} onClick={() => remove(reference)}>
              &#10005;
            </IconButton>
          </header>

          <div className={styles.entryBody}>
            <div className={styles.rowTwo}>
              <Field label="URL or repository path">
                {(id) => (
                  <>
                    <TextInput
                      id={id}
                      value={reference.locator}
                      maxLength={INTENT_LIMITS.referenceLocator}
                      placeholder="https://… or docs/orders.md"
                      onChange={(event) => update(reference.id, { locator: event.target.value })}
                    />
                    <span className={styles.locatorNote}>
                      {reference.locator.trim().length === 0
                        ? 'Optional.'
                        : isLinkableLocator(reference.locator)
                          ? 'Recorded as a link. Its contents are not read.'
                          : 'Recorded as text, not a link.'}
                    </span>
                  </>
                )}
              </Field>
              <Field label="Why it applies to this card">
                {(id) => (
                  <TextInput
                    id={id}
                    value={reference.relevance}
                    maxLength={INTENT_LIMITS.referenceRelevance}
                    onChange={(event) => update(reference.id, { relevance: event.target.value })}
                  />
                )}
              </Field>
            </div>

            <Field
              label="Excerpt to include"
              hint={
                reference.excerpt.trim().length === 0
                  ? 'Empty: this stays a reference only, and no content is carried into the prompt.'
                  : `${reference.excerpt.length.toLocaleString()} characters will be quoted in the prompt, labelled with this source.`
              }
            >
              {(id) => (
                <TextArea
                  id={id}
                  value={reference.excerpt}
                  maxLength={INTENT_LIMITS.referenceExcerpt}
                  placeholder="Paste the passage you want carried into the prompt"
                  onChange={(event) => update(reference.id, { excerpt: event.target.value })}
                />
              )}
            </Field>
          </div>
        </section>
      ))}

      <div>
        <Button disabled={atLimit} onClick={() => onChange([...references, newContextReference()])}>
          Add reference
        </Button>
        {atLimit ? (
          <span className={styles.locatorNote}> Limit of {INTENT_LIMITS.referenceCount} references reached.</span>
        ) : null}
      </div>
    </div>
  )
}
